import { Mail, Phone } from "lucide-react";
import Eyebrow from "@/components/Eyebrow";
import CTABanner from "@/components/CTABanner";
import { usePageMeta, useScrollReveal, COMPANY, SITE_URL } from "@/lib/site";

const ORG_REF = { "@id": `${SITE_URL}/#organization` };

const PERSON_LD = (name, jobTitle, email, telephone) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name,
  jobTitle,
  email,
  telephone,
  worksFor: ORG_REF,
  url: `${SITE_URL}/o-nas`
});

const TEAM = [
  {
    name: "Nikola Bystřická",
    role: "Jednatelka",
    email: "bystricka@ggaccounting.cz",
    phone: "+420 777 177 944",
    bio: "Vede G&G accounting od jejího založení. Specializuje se na komplexní účetnictví firem a živnostníků a na daňové poradenství.",
    initials: "NB",
  },
  {
    name: "Lenka Dočkalová",
    role: "Účetní",
    email: "dockalova@ggaccounting.cz",
    phone: "+420 775 257 440",
    bio: "Zkušená účetní s důrazem na pořádek v dokladech a včasné termíny. Klienti oceňují její trpělivost a srozumitelnou komunikaci.",
    initials: "LD",
  },
];

const VALUES = [
  { title: "Spolehlivost", desc: "Termíny dodržujeme. Vždy." },
  { title: "Srozumitelnost", desc: "Mluvíme jazykem firmy, ne paragrafu." },
  { title: "Osobní přístup", desc: "Znáte své účetní jménem. A oni znají vás." },
  { title: "Odbornost", desc: "Pracujeme pečlivě a v souladu se zákonem." },
];

export default function About() {
  usePageMeta(
    "O nás — G&G accounting",
    "Účetní firma s důrazem na osobní přístup a srozumitelnou komunikaci. Seznamte se s naším týmem.",
    {
      jsonLd: TEAM.map((m) => PERSON_LD(m.name, m.role, m.email, m.phone))
    }
  );
  useScrollReveal();

  return (
    <div>
      {/* HERO */}
      <section className="px-6 md:px-12 pt-20 md:pt-28 pb-20 bg-cream">
        <div className="max-w-7xl mx-auto reveal">
          <Eyebrow>O nás</Eyebrow>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium mt-6 leading-[0.98] max-w-4xl">
            Malý tým s velkým <span className="text-magenta">důrazem na detail.</span>
          </h1>
          <div className="mt-10 grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 space-y-5 text-base md:text-lg text-muted2 leading-relaxed">
              <p>
                G&amp;G accounting jsme založili s jasnou představou: účetnictví, které firmě
                pomáhá, ne komplikuje život. Žádné anonymní callcentrum, žádné fakturace „od stolu".
                Naopak — dlouhodobé vztahy, osobní setkávání a hluboké pochopení toho, co konkrétní
                firma řeší v provozu. Sídlíme v Brně-Slatině a věnujeme se klientům z Brna a okolí —
                díky tomu se s každým z nich můžeme i osobně setkat.
              </p>
              <p>
                Soustředíme se na malé a střední s.r.o. a OSVČ napříč obory — od služeb a obchodu
                přes řemesla a e-shopy až po výrobu a stavebnictví. Rozumíme tomu, jak se počítá
                zakázka, kde vznikají skryté náklady a jak na účetnictví dopadají větší investice.
                Každé firmě se věnujeme s porozuměním pro to, jak reálně funguje její provoz.
              </p>
              <p>
                Pracujeme v malém, sehraném týmu — díky tomu má každý klient svou stálou kontaktní
                osobu, která zná jeho firmu jako vlastní.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-square bg-ink2 rounded-md overflow-hidden">
                <img
                  src="https://www.ggfiltration.cz/liip/cache/big/uploads/dms//6752cdbe92443288946175.jpg"
                  alt="Tým G&G accounting — pohled do kanceláře v Brně"
                  width="900"
                  height="900"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="px-6 md:px-12 py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14 reveal">
            <Eyebrow>Náš tým</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
              Lidé za čísly.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-line border border-line rounded-md overflow-hidden">
            {TEAM.map((m, i) => (
              <div
                key={m.email}
                className="bg-white p-6 md:p-12 reveal"
                style={{ transitionDelay: `${i * 80}ms` }}
                data-testid={`team-card-${i}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-6">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-magenta text-white font-display text-xl md:text-2xl font-medium flex items-center justify-center shrink-0">
                    {m.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-2xl font-medium">{m.name}</h3>
                    <div className="text-xs md:text-sm text-magenta font-semibold uppercase tracking-wider mt-1">
                      {m.role}
                    </div>
                    <p className="text-sm text-muted2 leading-relaxed mt-4">{m.bio}</p>
                    <div className="mt-5 flex flex-col gap-2">
                      <a
                        href={`mailto:${m.email}`}
                        data-testid={`team-email-${i}`}
                        className="inline-flex items-start gap-2 text-sm font-medium text-ink hover:text-magenta transition-colors break-all"
                      >
                        <Mail className="w-4 h-4 shrink-0 mt-0.5" /> <span>{m.email}</span>
                      </a>
                      <a
                        href={`tel:${m.phone.replace(/\s/g, "")}`}
                        data-testid={`team-phone-${i}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-magenta transition-colors"
                      >
                        <Phone className="w-4 h-4 shrink-0" /> {m.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="px-6 md:px-12 py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-14 reveal">
            <Eyebrow>Naše hodnoty</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight font-medium mt-4 leading-[1.05]">
              Čtyři věci, na kterých <span className="text-magenta">stavíme.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line rounded-md overflow-hidden">
            {VALUES.map((v, i) => (
              <div key={v.title} className="bg-white p-8 md:p-10 reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="font-display text-4xl text-magenta font-medium mb-4">0{i + 1}</div>
                <h3 className="font-display text-xl font-medium mb-2">{v.title}</h3>
                <p className="text-sm text-muted2 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPANY INFO */}
      <section className="px-6 md:px-12 py-20 bg-white">
        <div className="max-w-7xl mx-auto reveal">
          <Eyebrow>Firemní údaje</Eyebrow>
          <div className="mt-6 grid md:grid-cols-3 gap-8 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted2 mb-1.5">Název</div>
              <div className="font-medium">{COMPANY.name}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted2 mb-1.5">IČO</div>
              <div className="font-medium">{COMPANY.ico}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted2 mb-1.5">Sídlo</div>
              <div className="font-medium">{COMPANY.address}</div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        eyebrow="Začněme spolu"
        title="Pojďme se nezávazně potkat."
        text="Schůzka u nás v kanceláři, ve vaší firmě nebo online — jak je vám libo."
        primaryHref="/kontakt"
        primaryLabel="Napsat nám"
      />
    </div>
  );
}
